import { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { MapPin, X } from 'lucide-react';
//import { flyToAndShowPin } from "./MapCommon";

export default function FloatingVideoPlayer({ url, stream, onClose, displayName, lng, lat, loopTimeLength, type }) {
  //for PTV call video display, and also for CCTV video footage
  const videoRef = useRef(null);

  const resizing = useRef(false);
  const resizeStart = useRef(null);

  const [useIframePlayer, setUseIframePlayer] = useState(false);

  const [size, setSize] = useState({ width: window.innerWidth / 3, height: window.innerHeight / 2 - 40 });
  const yOffset =
    displayName == 'Trident Resp 2'
      ? 20
      : displayName == 'Trident Resp 1'
        ? 20
        : displayName == 'Drone 1'
          ? window.innerHeight / 2
          : 20;

  const xOffset =
    displayName == 'Trident Resp 2'
      ? window.innerWidth / 3 - 15
      : displayName == 'Trident Resp 1'
        ? (window.innerWidth * 2.0) / 3 - 10
        : displayName == 'Drone 1'
          ? window.innerWidth / 3 + 100
          : displayName == 'Fire Truck 2'
            ? window.innerWidth / 3 - 15
            : displayName == 'Command Car 1'
              ? (window.innerWidth * 2.0) / 3 - 10
              : window.innerWidth - window.innerWidth / 3 - 10;

  const [position, setPosition] = useState({
    x: xOffset, // 10px margin from right edge
    y: yOffset, // 20px margin from top
  });

  const [loopNonce, setLoopNonce] = useState(0);
  useEffect(() => {
    if (!loopTimeLength) return;
    const id = setTimeout(() => setLoopNonce(n => n + 1), loopTimeLength * 1000);
    return () => clearTimeout(id);
  }, [loopNonce, loopTimeLength, url]); // restart timer after each remount / URL change

  const isWHEPStream = async url => {
    try {
      const response = await fetch(url, { method: 'GET' });
      const text = await response.text();
      return text.includes('RTCPeerConnection') || text.includes('whep');
    } catch (err) {
      console.warn('WHEP detection failed:', err);
      return false;
    }
  };
  useEffect(() => {
    if (url) {
      isWHEPStream(url).then(setUseIframePlayer);
    }
  }, [url]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    let hls;
    let isSubscribed = true;

    const startPlayback = async () => {
      try {
        if (stream) {
          // Clear any previous source first
          videoEl.src = '';
          videoEl.srcObject = stream;
        } else if (url) {
          if (Hls.isSupported() && url.includes('.m3u8')) {
            hls = new Hls();
            hls.loadSource(url);
            hls.attachMedia(videoEl);
          } else {
            videoEl.src = url;
          }
        }

        if (isSubscribed && videoRef.current && (videoEl.srcObject || videoEl.src)) {
          await videoEl.play();
        }
      } catch (err) {
        console.debug('Playback setup interrupted:', err);
      }
    };

    startPlayback();

    return () => {
      isSubscribed = false;
      if (hls) {
        hls.stopLoad();
        hls.detachMedia();
        hls.destroy();
      }
      // CRITICAL: Reset the video element entirely
      videoEl.pause();
      videoEl.srcObject = null;
      videoEl.src = '';
      videoEl.load(); // Forces the element to clear its internal state
    };
  }, [url, stream]);
  const positionRef = useRef(position);
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const onWindowMouseMove = e => {
    if (dragging.current) {
      setPosition({
        x: e.clientX - offset.current.x,
        y: Math.min(Math.max(e.clientY - offset.current.y, 0), window.innerHeight - 100),
      });
    } else if (resizing.current && resizeStart.current) {
      const dx = resizeStart.current.mouseX - e.clientX;
      const dy = e.clientY - resizeStart.current.mouseY;
      const newWidth = Math.max(240, resizeStart.current.startWidth + dx);
      const newHeight = resizeStart.current.startHeight + dy;

      setSize({
        width: newWidth,
        height: newHeight,
      });

      // ✅ keep bottom-right corner fixed, move left edge while resizing
      setPosition({
        x: e.clientX,
        y: resizeStart.current.startY,
      });
    }
  };

  const onWindowMouseUp = () => {
    dragging.current = false;
    resizing.current = false;
    document.body.style.userSelect = 'auto';
  };
  useEffect(() => {
    window.addEventListener('pointermove', onWindowMouseMove);
    window.addEventListener('pointerup', onWindowMouseUp);
    return () => {
      window.removeEventListener('pointermove', onWindowMouseMove);
      window.removeEventListener('pointerup', onWindowMouseUp);
    };
  }, []);
  return (
    <div
      className="absolute z-50 bg-black border border-gray-600 rounded shadow-lg"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height ?? 'auto',
        userSelect: dragging.current || resizing.current ? 'none' : 'auto',
        overflow: 'hidden',
      }}
    >
      <div
        className="bg-gray-800 text-white text-xs px-2 py-1 flex justify-between items-center cursor-move"
        onPointerDown={e => {
          dragging.current = true;
          document.body.style.userSelect = 'none';
          offset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y,
          };
        }}
      >
        <div className="flex items-center gap-2">
          <span className="truncate">{displayName}</span>
          {!stream && (
            <MapPin
              size={14}
              className="cursor-pointer text-white hover:text-green-500 transition-colors"
              onClick={() => {
                //flyToAndShowPin(lat, lng,null,mapControlRef.current?.mapRef.current);
              }}
            />
          )}
        </div>
        <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
          <X size={16} />
        </button>
      </div>
      {useIframePlayer ? (
        <iframe
          key={loopNonce}
          src={url}
          width="100%"
          height={size.height ? size.height - 30 : undefined}
          allow="autoplay"
          className="block"
        />
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          loop
          controls={!stream}
          className="w-full h-full object-contain"
        />
      )}
      <div
        className="absolute bg-red-50  bottom-[2px] left-[2px] w-[20px] h-[20px] cursor-nwse-resize z-[100]"
        onPointerDown={e => {
          e.preventDefault();
          e.stopPropagation();
          resizing.current = true;
          resizeStart.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            startWidth: size.width,
            startHeight: size.height,
            startX: positionRef.current.x,
            startY: positionRef.current.y,
          };
        }}
      />
    </div>
  );
}
